// @flow
import * as React from 'react';

import { FlipperContainer, RgbFlipper } from './styles';
import FlipperContent, { BrightnessFlipperIcon, FlipperMessage, Picker } from '../styles';

import BounceEffect from '~/components/widgets/bounce-effect';
import ColorPicker from './color-picker';
import devicesIcons from '~/core/constants/icons/devices-svg';
import DOMResize from '~/components/dom/dom-resize';
import { equals, inRange, noop } from 'lodash/fp';
import Flipper from '~/components/widgets/flipper';
import { FormattedMessage } from 'react-intl';
import messages from '~/core/i18n/components/widgets/flipper/intl';
import { withRouter } from 'react-router-dom';

const getIntlTemperatureKey = temp => {
    const tempMessageMap = new Map([
        [inRange(2700, 3461, temp), 'warmest'],
        [inRange(3461, 4221, temp), 'warm'],
        [inRange(4221, 4981, temp), 'mid'],
        [inRange(4981, 5741, temp), 'cool'],
        [inRange(5741, 6536, temp), 'coolest']
    ]);

    return tempMessageMap.get(true) || 'warmest';
};

const makeFlipperMessage = message => <FlipperMessage>{message}</FlipperMessage>;

type Props = {
    bulbBrightness: number,
    changeColor: noop,
    debug?: boolean,
    delayRendering?: number,
    handleRadiusPercent: number,
    image: string,
    isCustomRange: boolean,
    isGroup: boolean,
    isOn: boolean,
    isSaving?: boolean,
    isSmall: boolean,
    isToggling?: boolean,
    logger?: noop,
    colourStartRadians: number,
    colourStopRadians: number,
    maxTemperature?: number,
    minTemperature?: number,
    pickerDepthPercent: number,
    productId?: string,
    rangeValue?: number,
    rgb?: Uint8ClampedArray,
    toggleLightbulb: noop
};

type State = {
    rgb?: ?Uint8ClampedArray
};

class RGBFlipper extends React.Component<Props, State> {
    state = {};

    constructor(props: Props) {
        super(props);

        if (props.rgb) {
            this.state = {
                rgb: props.rgb
            };
        }
    }

    componentWillReceiveProps(nextProps: Props) {
        if (
            !equals(nextProps.rgb, this.props.rgb) ||
            nextProps.isOn !== this.props.isOn ||
            nextProps.isCustomRange !== this.props.isCustomRange
        ) {
            this.setState({
                rgb: nextProps.rgb
            });
        }
    }

    render() {
        const {
            isOn,
            isSaving,
            isToggling,
            toggleLightbulb,
            changeColor,
            bulbBrightness,
            debug,
            delayRendering,
            handleRadiusPercent,
            image,
            isCustomRange,
            logger,
            colourStartRadians,
            colourStopRadians,
            pickerDepthPercent,
            rangeValue,
            rgb,
            minTemperature,
            maxTemperature,
            productId,
            isSmall,
            isGroup
        } = this.props;

        const temperatureIntlKey = getIntlTemperatureKey(rangeValue);

        return (
            <RgbFlipper>
                <DOMResize>
                    {({ width }) => (
                        <Picker isOn={isOn}>
                            <ColorPicker
                                colourStartRadians={colourStartRadians}
                                colourStopRadians={colourStopRadians}
                                debug={debug}
                                delayRendering={delayRendering}
                                disabled={!isOn}
                                handleRadiusPercent={handleRadiusPercent}
                                image={image}
                                isCustomRange={isCustomRange}
                                logger={logger}
                                maxTemperature={maxTemperature}
                                minTemperature={minTemperature}
                                onValue={v => {
                                    if (v.shouldUpdateServer) {
                                        changeColor(v);
                                    }

                                    this.setState({
                                        rgb: v.rgb
                                    });
                                }}
                                pickerDepthPercent={pickerDepthPercent}
                                productId={productId}
                                rangeValue={rangeValue}
                                rgb={rgb}
                                width={width}
                            />
                        </Picker>
                    )}
                </DOMResize>

                <FlipperContainer>
                    <Flipper childKey={isOn ? 'show' : 'hide'}>
                        <div>
                            <BounceEffect isWaiting={isToggling || isSaving}>
                                <FlipperContent
                                    isActive={isOn}
                                    isCustomRange={isCustomRange}
                                    isOn={isOn}
                                    isSmall={isSmall}
                                    isVariableColour={!isCustomRange}
                                    onClick={toggleLightbulb}
                                    rgb={this.state.rgb}
                                    style={{
                                        background:
                                            isOn && !isCustomRange && this.state.rgb
                                                ? `rgb(${this.state.rgb.toString()})`
                                                : '#FFFFFF'
                                    }}
                                    tabIndex="0"
                                >
                                    <BrightnessFlipperIcon
                                        isActive={isOn}
                                        isVariableColour={!isCustomRange}
                                        type={isGroup ? devicesIcons.LIGHT_DOUBLE : devicesIcons.LIGHT}
                                    />
                                    {isOn ? (
                                        <FormattedMessage
                                            {...messages.on}
                                            values={{
                                                brightness: bulbBrightness && bulbBrightness.toFixed()
                                            }}
                                        >
                                            {makeFlipperMessage}
                                        </FormattedMessage>
                                    ) : (
                                        <FormattedMessage {...messages.off}>{makeFlipperMessage}</FormattedMessage>
                                    )}
                                    {isOn &&
                                        isCustomRange && (
                                            <FormattedMessage {...messages[temperatureIntlKey]}>
                                                {makeFlipperMessage}
                                            </FormattedMessage>
                                        )}
                                </FlipperContent>
                            </BounceEffect>
                        </div>
                    </Flipper>
                </FlipperContainer>
            </RgbFlipper>
        );
    }
}

export default withRouter(RGBFlipper);



// WEBPACK FOOTER //
// ./src/components/widgets/flipper/rgb-flipper/component.js